<script setup lang="ts">
/**
 * Email updated (Figma `3603:68523` / `3603:68568`).
 *
 * Opened from email 04, sent to the new address. After success the old address
 * gets email 05. Expired: nothing changed, the current address stays active and
 * the change can be started again in Personal data.
 */
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { FairButton } from '@/ui/primitives/FairButton'
import { useAuthModal, useTokenStore } from '@/features/auth/store'
import AuthScreenShell from '@/features/auth/ui/parts/AuthScreenShell.vue'
import { PROFILE_PERSONAL_DATA_ROUTE } from '@/router/routes'
import successUrl from '@/assets/img/auth/illustration-password-updated.png'
import expiredUrl from '@/assets/img/auth/illustration-link-expired.png'
import { useEmailLinkStore } from '../store'
import { useSignedInAction } from './useSignedInAction'

const { t } = useI18n()
const router = useRouter()
const authModal = useAuthModal()
const tokenStore = useTokenStore()
const links = useEmailLinkStore()
const runSignedIn = useSignedInAction()

const expired = computed(() => links.updatedStatus === 'expired')

function onContinue() {
  if (tokenStore.isAuthenticated()) {
    authModal.hideAuthModal()
    return
  }
  authModal.showAuthModal('login')
}

function goToProfile() {
  runSignedIn(() => {
    authModal.hideAuthModal()
    router.push(PROFILE_PERSONAL_DATA_ROUTE)
  })
}
</script>

<template>
  <AuthScreenShell
    v-if="expired"
    centered
    :title="t('email_links.updated.expired.title')"
    :description="t('email_links.updated.expired.description')"
  >
    <template #art>
      <img
        class="fair-auth-screen__art"
        :src="expiredUrl"
        alt=""
      >
    </template>

    <FairButton
      class="fair-auth-cta"
      size="auto"
      :label="t('email_links.updated.expired.cta')"
      @click="goToProfile"
    />
    <p class="fair-auth-switch">
      <button
        type="button"
        class="fair-auth-form__link"
        @click="authModal.showAuthModal('login')"
      >
        {{ t('auth.link_expired.back_to_login') }}
      </button>
    </p>
  </AuthScreenShell>

  <AuthScreenShell
    v-else
    centered
    :title="t('email_links.updated.success.title')"
    :description="t('email_links.updated.success.description', { email: links.updatedEmail })"
  >
    <template #art>
      <img
        class="fair-auth-screen__art"
        :src="successUrl"
        alt=""
      >
    </template>

    <FairButton
      class="fair-auth-cta"
      size="auto"
      :label="t('email_links.continue')"
      @click="onContinue"
    />
  </AuthScreenShell>
</template>
